import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Send } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const FIELDS = [
  { name: "full_name", label: "Vor- und Nachname", type: "text", auto: "name" },
  { name: "phone", label: "Telefon", type: "tel", auto: "tel" },
  { name: "email", label: "E-Mail", type: "email", auto: "email" },
  { name: "postal_code", label: "PLZ / Ort", type: "text", auto: "postal-code" },
];

export const ContactStep = ({ answers, onDone }) => {
  const [form, setForm] = useState({ full_name: "", phone: "", email: "", postal_code: "", notes: "", website: "" });
  const [consent, setConsent] = useState(false);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!consent) return toast.error("Bitte stimmen Sie der Kontaktaufnahme zu.");
    setLoading(true);
    try {
      await axios.post(`${API}/leads`, { ...answers, ...form });
      onDone();
    } catch {
      toast.error("Senden fehlgeschlagen. Bitte prüfen Sie Ihre Angaben und versuchen Sie es erneut.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.form onSubmit={submit} data-testid="contact-form" initial={{ opacity: 0, x: 40 }} animate={{ opacity: 1, x: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}>
      <h3 className="font-display text-2xl sm:text-4xl font-medium tracking-[-0.02em]">Wohin dürfen wir uns melden?</h3>
      <input type="text" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website} onChange={set("website")} className="absolute -left-[9999px] h-0 w-0 opacity-0" />
      <div className="mt-8 grid gap-4 sm:grid-cols-2">
        {FIELDS.map((f) => (
          <label key={f.name} className="block">
            <span className="text-sm text-[var(--mist)]">{f.label} *</span>
            <Input required type={f.type} autoComplete={f.auto} value={form[f.name]} onChange={set(f.name)} data-testid={`contact-${f.name}-input`} className="mt-2 h-12 rounded-xl border-white/15 bg-[#04171B] text-[var(--foam)] focus-visible:ring-[var(--lagoon)]" />
          </label>
        ))}
        <label className="block sm:col-span-2">
          <span className="text-sm text-[var(--mist)]">Anmerkung zu Ihrem Pool (optional)</span>
          <Textarea value={form.notes} onChange={set("notes")} data-testid="contact-notes-input" className="mt-2 min-h-[96px] rounded-xl border-white/15 bg-[#04171B] text-[var(--foam)] focus-visible:ring-[var(--lagoon)]" />
        </label>
      </div>
      <label className="mt-5 flex items-start gap-3 text-sm text-[var(--mist)]">
        <Checkbox data-testid="contact-consent-checkbox" checked={consent} onCheckedChange={(v) => setConsent(!!v)} className="mt-0.5 border-white/30 data-[state=checked]:bg-[var(--lagoon)] data-[state=checked]:text-[#04171B]" />
        <span>Ich bin einverstanden, dass meine Angaben zur Bearbeitung meiner Anfrage gespeichert und ich kontaktiert werde. Hinweise in der <a href="/datenschutz" target="_blank" rel="noopener noreferrer" data-testid="contact-datenschutz-link" className="underline">Datenschutzerklärung</a>.</span>
      </label>
      <button type="submit" disabled={loading} data-testid="submit-lead-btn" className="btn-primary mt-8 w-full justify-center sm:w-auto">
        {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Kostenlosen Pool-Check absenden
      </button>
    </motion.form>
  );
};
